import type { SecureNoteContent } from '@frontend/api-types';
import type { StrictOmit } from '@frontend/duck-tape';
import { isEqual } from '@frontend/duck-tape';
import { makeSavedFileList, validators } from '../utils';
import { makeFormComponent, makeFormHook } from '../utils/factory';
import type { InferFormType } from '../utils/types';

export const secureNoteFormSchema = validators.object({
  attachments: validators.fileArrayValidator('localOrSavedFile'),
  notes: validators.optionalString(),
  password: validators.optionalString(),
  title: validators.requiredString(),
  username: validators.optionalString(),
  website: validators.looseUrlValidator({ isOptional: true }),
});

export type SecureNoteFormSchema = InferFormType<typeof secureNoteFormSchema>;

export const getSecureNoteFormDefaultValues = (
  secureNote?: MaybeUndefined<SecureNoteContent & { title: string }>,
): SecureNoteFormSchema => ({
  attachments: makeSavedFileList(secureNote?.attachments ?? [], (a) => a),
  notes: secureNote?.notes ?? '',
  password: secureNote?.password ?? '',
  title: secureNote?.title ?? '',
  username: secureNote?.username ?? '',
  website: secureNote?.website ?? '',
});

export const useSecureNoteForm = makeFormHook(secureNoteFormSchema, getSecureNoteFormDefaultValues());

export const SecureNoteForm = makeFormComponent(secureNoteFormSchema, getSecureNoteFormDefaultValues());

export const checkDidSecureNoteFormValuesChange = (
  originalValues: SecureNoteContent,
  newValues: StrictOmit<SecureNoteFormSchema, 'title'>,
): boolean => {
  const newAttachmentKeys = newValues.attachments.map((a) => (a.__typename === 'SavedFile' ? a.key : a.name));
  const originalAttachmentKeys = originalValues?.attachments?.map((a) => a.key) ?? [];
  if (newValues.attachments.find((a) => a.__typename === 'LocalFile')) return true;
  return (
    !isEqual(newAttachmentKeys, originalAttachmentKeys) ||
    newValues.notes !== originalValues.notes ||
    newValues.password !== originalValues.password ||
    newValues.username !== originalValues.username ||
    newValues.website !== originalValues.website
  );
};

export const isSecureNoteEmpty = (secureNote: SecureNoteContent | SecureNoteFormSchema) =>
  !secureNote?.attachments?.length &&
  !secureNote?.notes &&
  !secureNote?.password &&
  !secureNote?.username &&
  !secureNote?.website;
